"use client";

import { useState } from "react";
import Image from "next/image";
import { Bell, ListPlus, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRODUCT_LCP_SIZES, shouldSkipOptimization } from "@/lib/image-host";

interface Props {
  images: string[];
  alt: string;
  onOpenAlert: () => void;
  onAddToList?: () => void;
  className?: string;
}

const VISIBLE = 4;

export function ProductImageGallery({ images, alt, onOpenAlert, onAddToList, className }: Props) {
  const list = images.length ? images : ["/placeholder-product.svg"];
  const [activeIdx, setActiveIdx] = useState(0);
  const [start, setStart] = useState(0);
  // En 4G las 4 miniaturas del viewport competían con el LCP. No pedimos
  // bytes de thumbs hasta que el hero terminó (o el usuario cambia de foto).
  const [thumbsReady, setThumbsReady] = useState(false);

  const canPrev = start > 0;
  const canNext = start + VISIBLE < list.length;

  return (
    <div className={cn("flex gap-3", className)}>
      <div className="flex flex-col items-center gap-1 w-20 shrink-0">
        <button
          type="button"
          onClick={() => canPrev && setStart((s) => Math.max(0, s - 1))}
          disabled={!canPrev}
          className="w-8 h-8 rounded-full border bg-background disabled:opacity-30 hover:bg-muted flex items-center justify-center"
          aria-label="Ver imágenes anteriores"
        >
          <ChevronUp className="w-4 h-4" aria-hidden="true" />
        </button>

        <div className="flex flex-col gap-2">
          {list.slice(start, start + VISIBLE).map((src, i) => {
            const idx = start + i;
            return (
              <button
                key={`${src}-${idx}`}
                type="button"
                onClick={() => {
                  setActiveIdx(idx);
                  setThumbsReady(true);
                }}
                aria-label={`Mostrar imagen ${idx + 1} de ${list.length}`}
                aria-current={activeIdx === idx}
                className={cn(
                  "relative w-20 h-20 rounded-xl overflow-hidden border-2 bg-muted/40 transition-colors",
                  activeIdx === idx ? "border-primary" : "border-transparent hover:border-muted-foreground/40"
                )}
              >
                {thumbsReady ? (
                  <Image
                    src={src}
                    alt=""
                    fill
                    sizes="80px"
                    className="object-contain p-1.5"
                    loading="lazy"
                    fetchPriority="low"
                    quality={40}
                    unoptimized={shouldSkipOptimization(src)}
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => canNext && setStart((s) => s + 1)}
          disabled={!canNext}
          className="w-8 h-8 rounded-full border bg-background disabled:opacity-30 hover:bg-muted flex items-center justify-center"
          aria-label="Ver imágenes siguientes"
        >
          <ChevronDown className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      <div className="relative aspect-square flex-1 bg-background rounded-lg overflow-hidden">
        <Image
          src={list[activeIdx]}
          alt={alt}
          fill
          sizes={PRODUCT_LCP_SIZES}
          className="object-contain"
          // 60: en Lighthouse móvil (DPR~1.75) el hero ~512px; 75 gastaba RTT
          // sin ganar percepción. Prioridad alta + thumbs diferidos ganan LCP.
          quality={60}
          priority
          fetchPriority="high"
          decoding="async"
          onLoad={() => setThumbsReady(true)}
          unoptimized={shouldSkipOptimization(list[activeIdx])}
        />
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={onOpenAlert}
            className="w-10 h-10 bg-background rounded-lg border shadow-xs hover:bg-muted flex items-center justify-center"
            aria-label="Configurar alerta de precios"
            title="Configurar alerta de precios"
          >
            <Bell className="w-4 h-4" />
          </button>
          {onAddToList && (
            <button
              type="button"
              onClick={onAddToList}
              className="w-10 h-10 bg-background rounded-lg border shadow-xs hover:bg-muted flex items-center justify-center"
              aria-label="Añadir a lista"
              title="Añadir a lista"
            >
              <ListPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
